import { defineStore, storeToRefs } from 'pinia'
import {
  LOG_LEVEL,
  Purchases as CapacitorPurchases,
  type PurchasesPackage as NativePackage,
  type PurchasesOfferings as NativeOfferings,
  type PurchasesOffering as NativeOffering,
  type PurchasesConfiguration,
  PACKAGE_TYPE,
} from '@revenuecat/purchases-capacitor'
import {
  Purchases as WebPurchases,
  type Package,
  type Offerings,
  type Offering,
} from '@revenuecat/purchases-js'
import { Capacitor } from '@capacitor/core'
import { useAuthStore } from '@/stores/auth.ts'
import { useInfoStore } from '@/stores/info.ts'
import { ref, toRaw, watch } from 'vue'
import { useLocalizationStore } from '@/stores/localization.ts'

export type PaymentProduct = {
  id: string
  identifier: string
  priceString: string
  periodType: 'yearly' | 'monthly'
}

export type PaymentCustomerInfo = {
  activeSubscriptions: string[]
}

export const usePaymentStore = defineStore('payment', () => {
  const { authState } = storeToRefs(useAuthStore())
  const { isWeb } = useInfoStore()
  const { language } = useLocalizationStore()
  const initialized = ref(false)
  const customerInfo = ref<PaymentCustomerInfo | null>(null)

  const webPackages = ref<Package[]>([])
  const nativePackages = ref<NativePackage[]>([])

  function resolveWebOffering(offerings: Offerings): Offering | null {
    if (offerings.current) return offerings.current
    const allOfferings = Object.values(offerings.all)
    return allOfferings[0] ?? null
  }

  function resolveNativeOffering(offerings: NativeOfferings): NativeOffering | null {
    if (offerings.current) return offerings.current
    const allOfferings = Object.values(offerings.all)
    return allOfferings[0] ?? null
  }

  function packageToPeriodType(packageType: string): 'yearly' | 'monthly' {
    if (packageType === '$rc_annual' || packageType === PACKAGE_TYPE.ANNUAL) return 'yearly'
    return 'monthly'
  }

  async function getProducts(): Promise<{ products: PaymentProduct[] }> {
    if (!initialized.value) {
      await init()
    }
    if (!initialized.value) {
      return { products: [] }
    }
    if (isWeb) {
      const instance = WebPurchases.getSharedInstance()
      const offerings = await instance.getOfferings()
      const offering = resolveWebOffering(offerings)
      const packages = offering?.availablePackages ?? []
      webPackages.value = packages
      return {
        products: packages.map((pkg) => ({
          id: pkg.identifier,
          identifier: pkg.webBillingProduct.identifier,
          priceString: pkg.webBillingProduct.price.formattedPrice,
          periodType: packageToPeriodType(pkg.packageType),
        })),
      }
    }
    const offerings = await CapacitorPurchases.getOfferings()
    const offering = resolveNativeOffering(offerings)
    const packages = offering?.availablePackages ?? []
    nativePackages.value = packages
    return {
      products: packages.map((pkg) => ({
        id: pkg.identifier,
        identifier: pkg.product.identifier,
        priceString: pkg.product.priceString,
        periodType: packageToPeriodType(pkg.packageType),
      })),
    }
  }

  async function getCustomerInfo() {
    if (isWeb) {
      return await WebPurchases.getSharedInstance().getCustomerInfo()
    }
    return await CapacitorPurchases.getCustomerInfo()
  }

  async function makePurchase(product: PaymentProduct, oldProduct?: PaymentProduct) {
    if (!initialized.value) {
      await init()
    }
    if (isWeb) {
      const pkg = webPackages.value.find(
        (p) => p.webBillingProduct.identifier === product.identifier,
      )
      if (!pkg) {
        throw new Error(`Package not found for product: ${product.identifier}`)
      }
      const instance = WebPurchases.getSharedInstance()
      const result = await instance.purchase({
        rcPackage: pkg,
        skipSuccessPage: true,
        selectedLocale: language === 'de' ? 'de' : 'en',
      })
      const webInfo = await instance.getCustomerInfo()
      customerInfo.value = {
        activeSubscriptions: Array.from(webInfo.activeSubscriptions),
      }
      return result
    }
    const nativePackage = nativePackages.value.find(
      (pkg) => pkg.product.identifier === product.identifier,
    )
    if (!nativePackage) {
      throw new Error(`Native package not found for: ${product.identifier}`)
    }
    try {
      return await CapacitorPurchases.purchasePackage({
        aPackage: toRaw(nativePackage),
        ...(oldProduct
          ? {
              googleProductChangeInfo: {
                oldProductIdentifier: oldProduct.identifier,
              },
            }
          : {}),
      })
    } catch (error) {
      console.error('Error making purchase:', error)
      throw error
    }
  }

  watch(authState, async () => {
    if (initialized.value && authState.value?.userAdmin?.user?.id) {
      if (isWeb) {
        await WebPurchases.getSharedInstance().changeUser(authState.value.userAdmin.user.id)
        const webInfo = await WebPurchases.getSharedInstance().getCustomerInfo()
        customerInfo.value = {
          activeSubscriptions: Array.from(webInfo.activeSubscriptions),
        }
      } else {
        await loginUserToPayment(authState.value.userAdmin.user.id)
        const result = await CapacitorPurchases.getCustomerInfo()
        customerInfo.value = {
          activeSubscriptions: result.customerInfo.activeSubscriptions,
        }
      }
    }
  })

  async function init() {
    // Offline: RevenueCat configure/getCustomerInfo hit the network and would
    // throw at boot. Skip — premium comes from the cached UserAdmin, and no
    // purchase can happen offline anyway. Re-runs online on next launch.
    if (useInfoStore().isOffline) return
    if (isWeb) {
      const appUserId = authState.value?.userAdmin?.user?.id
      if (!appUserId) return
      WebPurchases.configure({
        apiKey: import.meta.env.VITE_REVENUE_CAT_WEB_API_KEY,
        appUserId,
      })
      initialized.value = true
      const webInfo = await WebPurchases.getSharedInstance().getCustomerInfo()
      customerInfo.value = {
        activeSubscriptions: Array.from(webInfo.activeSubscriptions),
      }
      return
    }

    await CapacitorPurchases.setLogLevel({ level: LOG_LEVEL.DEBUG })
    const nativeApiKey =
      Capacitor.getPlatform() === 'ios'
        ? import.meta.env.VITE_REVENUE_CAT_IOS_API_KEY
        : import.meta.env.VITE_REVENUE_CAT_API_KEY
    const purchaseConfig: PurchasesConfiguration = {
      apiKey: nativeApiKey,
    }
    if (authState.value?.userAdmin?.user?.id) {
      purchaseConfig.appUserID = authState.value.userAdmin.user.id
    }
    await CapacitorPurchases.configure(purchaseConfig)
    initialized.value = true
    if (authState.value?.userAdmin?.user?.id) {
      const result = await CapacitorPurchases.getCustomerInfo()
      customerInfo.value = {
        activeSubscriptions: result.customerInfo.activeSubscriptions,
      }
    }
    await CapacitorPurchases.addCustomerInfoUpdateListener((update) => {
      customerInfo.value = {
        activeSubscriptions: update.activeSubscriptions,
      }
    })
  }

  async function loginUserToPayment(userId: string) {
    try {
      await CapacitorPurchases.logIn({ appUserID: userId })
    } catch (error) {
      console.error('Error logging in user to payment:', error)
    }
  }

  return {
    getProducts,
    init,
    getCustomerInfo,
    makePurchase,
    customerInfo,
  }
})
